import React, { useMemo } from 'react';
import { ShopTypes } from '~/lib/data-contract';
import { useTranslations } from '~/services/umbraco/hooks';
import { SkeletonLoader } from '~/shared/components/SkeletonLoader';
import {
    Brand,
    ComplementaryProductsLink,
    Header,
    ReadMore,
    Sku,
    SkuContainer,
    Splash,
    SplashesWrapper,
    StyledBrandLink,
    StyledLink,
    Text,
    Wrapper,
} from './styled';
import { theme } from '~/theme';

type Props = {
    brandUrl: string | undefined;
    campaigns: ShopTypes.ActiveCampaignDetails[] | undefined;
    pluralPrices: string | null;
    product: ShopTypes.ProductQueryProduct;
    onReadMoreClick?: (event: React.MouseEvent) => void;
    onComplementaryProductsClick?: (event: React.MouseEvent) => void;
    hasComplementaryProducts: boolean;
};

const WEBSITE_URL = process.env.NEXT_PUBLIC_WEBSITE_URL;

export default function ProductInfo({
    brandUrl,
    pluralPrices,
    product,
    onReadMoreClick,
    onComplementaryProductsClick,
    hasComplementaryProducts,
    campaigns,
}: Props) {
    const { translate } = useTranslations();
    const info = product?.item || product;
    const productName = `${info?.name ? `${info?.name} ` : ''}${info?.name2 ?? ''}`;
    const productSku = product.item?.itemNumber || product.primaryItemNumber;
    const description = product.shortDescription ?? product.longDescription;
    const brandHref =
        WEBSITE_URL && brandUrl
            ? WEBSITE_URL +
              brandUrl +
              '/' +
              product.brand
                  ?.replace(' ', '-')
                  .replace('æ', 'ae')
                  .replace('ø', 'oe')
                  .replace('å', 'aa')
                  .replace('ö', 'oe')
                  .replace('ä', 'ae')
                  .replace('ü', 'ue')
                  .toLowerCase()
            : null;

    const campaignSplashes = useMemo(() => {
        if (!campaigns) {
            return;
        }
        const splashMap = new Map<string, ShopTypes.ActiveCampaignDetails>();
        campaigns.forEach((campaign) => {
            if (splashMap.has(String(campaign.entityId)) ?? !campaign.labelDescription) {
                return;
            }
            splashMap.set(String(campaign.entityId), campaign);
        });
        return Array.from(splashMap.values());
    }, [campaigns]);

    return (
        <Wrapper>
            {campaigns || pluralPrices ? (
                <SplashesWrapper aria-label="Product Splashes">
                    {campaigns
                        ? campaignSplashes?.map((campaign) =>
                              campaign.url && campaign.labelDescription ? (
                                  <Splash
                                      key={campaign.entityId}
                                      style={{
                                          backgroundColor: `#${campaign.labelColorCode}`,
                                          color: `#${campaign.fontColorCode}`,
                                      }}
                                  >
                                      {campaign.url?.startsWith('/') ? (
                                          <StyledLink
                                              href={campaign.url}
                                              target="_self"
                                              prefetch={false}
                                          >
                                              {campaign.labelDescription}
                                          </StyledLink>
                                      ) : (
                                          <a
                                              id="campaignSplash"
                                              style={{
                                                  color: `#${campaign.fontColorCode}`,
                                                  backgroundColor: `#${campaign.labelColorCode}`,
                                              }}
                                              href={campaign.url}
                                              target="_blank"
                                          >
                                              {campaign.labelDescription}
                                          </a>
                                      )}
                                  </Splash>
                              ) : null,
                          )
                        : null}

                    {pluralPrices && pluralPrices.length > 0 ? (
                        <Splash
                            key="pluralSplash"
                            style={{
                                backgroundColor: theme.colors.orange,
                                color: theme.colors.black,
                            }}
                        >
                            {pluralPrices}
                        </Splash>
                    ) : null}
                </SplashesWrapper>
            ) : null}

            {productName ? (
                <Header as="h1" variant="display3">
                    {productName}
                </Header>
            ) : null}

            {product.brand ? (
                brandHref ? (
                    <StyledBrandLink href={brandHref} animation="reverse">
                        {product.brand}
                    </StyledBrandLink>
                ) : (
                    <Brand as="h2" variant="paragraph">
                        {product.brand}
                    </Brand>
                )
            ) : null}

            <SkuContainer>
                {productSku ? (
                    <Sku as="p" variant="paragraph">
                        {productSku}
                    </Sku>
                ) : null}
            </SkuContainer>

            {description ? (
                <>
                    <Text as={'p'} variant="paragraph">
                        {description}
                    </Text>

                    <ReadMore href="#description" onClick={onReadMoreClick}>
                        {translate('product.readMore')}
                    </ReadMore>
                </>
            ) : null}

            {hasComplementaryProducts ? (
                <ComplementaryProductsLink
                    href="#complementary"
                    onClick={onComplementaryProductsClick}
                >
                    {translate('product.viewComplementaryProducts')}
                </ComplementaryProductsLink>
            ) : null}
        </Wrapper>
    );
}

const Skeleton = () => (
    <>
        <SkeletonLoader height={16} width={60} isFullWidth style={{ marginBottom: 10 }} />
        <SkeletonLoader height={35} width={180} isFullWidth style={{ marginBottom: 5 }} />
        <SkeletonLoader height={35} width={250} style={{ marginBottom: 25 }} />

        <SkuContainer>
            <SkeletonLoader height={15} width={100} style={{ marginBottom: 5 }} />
            <SkeletonLoader height={15} width={100} style={{ marginBottom: 5 }} />
        </SkuContainer>
        <SkeletonLoader height={100} width={300} style={{ marginBottom: 5 }} />
    </>
);

ProductInfo.Skeleton = Skeleton;
